/**
 * Shared types used across multiple features.
 *
 * Only cross-cutting API models belong here.
 * Feature-specific types live in their own feature modules.
 */

// ── ProblemDetails — RFC 9457 structured error response ──────────────────────

/** RFC 9457 compliant error response returned by the backend. */
export interface ProblemDetails {
    type: string
    title: string
    status: number
    detail?: string
    instance?: string
    errors?: Record<string, string[]>
    [key: string]: unknown
}

/** Type guard to check if an error response contains ProblemDetails. */
export function isProblemDetails(obj: unknown): obj is ProblemDetails {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'type' in obj &&
        'title' in obj &&
        'status' in obj
    )
}

// ── Pagination — mirrors backend PagedRequest / PagedResponse ────────────────

/** Parameters for requesting a page of results. */
export interface PagedRequest {
    page?: number
    per_page?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    search?: string
    filters?: Record<string, string | string[]>
}

/** Paginated API response wrapper. */
export interface PagedResponse<T> {
    items: T[]
    total: number
    page: number
    per_page: number
    pages: number
}

/**
 * Convert a PagedRequest object to URLSearchParams.
 * Omits keys whose values are undefined or null.
 */
export function buildPagedParams(req?: PagedRequest): URLSearchParams {
    const params = new URLSearchParams()
    if (!req) return params

    if (req.page != null) params.set('page', String(req.page))
    if (req.per_page != null) params.set('per_page', String(req.per_page))
    if (req.sort_by) params.set('sort_by', req.sort_by)
    if (req.sort_order) params.set('sort_order', req.sort_order)
    if (req.search) params.set('search', req.search)
    if (req.filters) {
        for (const [key, val] of Object.entries(req.filters)) {
            if (val !== undefined && val !== null) {
                params.set(key, Array.isArray(val) ? val.join(',') : val)
            }
        }
    }

    return params
}
